import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie, ReferenceLine, AreaChart, Area
} from 'recharts';
import { 
  Clock, CheckCircle, RefreshCw, AlertTriangle, Users, Phone, 
  TrendingUp, TrendingDown, Filter, ChevronDown, ChevronUp, 
  Search, Download, Settings, MessageSquare, Headphones,
  UserCheck, Building2, Wrench, HelpCircle, Target, AlertCircle,
  Info, X, BookOpen, Star
} from 'lucide-react';
import { toast } from 'sonner';
import TicketSearchByEmail from './atencion360/TicketSearchByEmail';
import { secureExportDownload } from '../utils/secureExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// GLOSARIO DE KPIs - Explicaciones en "criollo"
// =============================================================================
const KPI_GLOSSARY = {
  frt: {
    sigla: 'FRT',
    nombre: 'Tiempo a la 1ª Respuesta',
    descripcion: 'Cuánto tarda el equipo en contestar un ticket por primera vez. Medimos la MEDIANA (el valor del medio) para evitar que casos extremos distorsionen.',
    formula: 'Mediana del tiempo entre creación del ticket y primera respuesta del agente',
    objetivo: '≤ 4 horas (editable)',
    nota: 'Tiempo CALENDARIO (incluye fines de semana y feriados), no horas hábiles. El objetivo es configurable desde la pestaña Objetivos.'
  },
  ttr: {
    sigla: 'TTR',
    nombre: 'Tiempo de Resolución',
    descripcion: 'Cuánto tarda en cerrarse/resolverse un ticket desde que se abre. También usamos MEDIANA.',
    formula: 'Mediana del tiempo entre creación y cierre del ticket',
    objetivo: '< 48 horas',
    nota: 'Tiempo CALENDARIO. El P90 indica que el 90% de los tickets se resuelven en ese tiempo o menos.'
  },
  fcr: {
    sigla: 'FCR',
    nombre: 'Resolución en 1er Contacto',
    descripcion: '¿Qué porcentaje de tickets se resuelven de una, sin necesidad de ida y vuelta? Cuanto más alto, mejor.',
    formula: '% de tickets marcados como "one touch" (resueltos en un solo contacto)',
    objetivo: '>= 70%',
    nota: 'Un FCR alto indica eficiencia y buena capacitación del equipo.'
  },
  reopen: {
    sigla: 'Reapertura',
    nombre: 'Tasa de Reapertura',
    descripcion: '¿Qué porcentaje de tickets cerrados se vuelven a abrir? Un número alto indica que no se resolvió bien la primera vez.',
    formula: '% de tickets con fecha de reapertura / total de tickets',
    objetivo: '< 5%',
    nota: 'Reaperturas frecuentes afectan la satisfacción del alumno.'
  },
  escalamiento: {
    sigla: 'Escalamiento',
    nombre: '% Escalado a N1',
    descripcion: '¿Qué porcentaje de tickets requieren escalarse a un nivel superior (N1 = soporte técnico/especializado)?',
    formula: '% de tickets en nivel N1 / total con nivel asignado',
    objetivo: 'Variable según complejidad',
    nota: 'Un escalamiento muy alto puede indicar falta de capacitación en N0.'
  },
  nps: {
    sigla: 'NPS',
    nombre: 'Net Promoter Score',
    descripcion: '¿Recomendaría la experiencia? Se calcula restando el % de detractores (0-6) del % de promotores (9-10).',
    formula: '% Promotores (9-10) − % Detractores (0-6)',
    objetivo: '> 50 es excelente',
    nota: 'Rango: -100 a +100. En el contexto de agentes, es una SEÑAL INDIRECTA (NPS del alumno, no del agente).'
  },
  csat: {
    sigla: 'CSAT',
    nombre: 'Satisfacción Post-Ticket',
    descripcion: 'Porcentaje de respuestas satisfechas (rating = 2 en escala 0-2). Fuente: HubSpot hs_last_csat_rating.',
    formula: '% Top-box = respuestas con rating = 2 / total respuestas × 100',
    objetivo: '>= 85%',
    nota: 'Escala HubSpot: 0=👎 Insatisfecho, 1=😐 Neutral, 2=👍 Satisfecho. Tasa de respuesta baja = dato poco representativo.'
  },
  sla: {
    sigla: 'SLA',
    nombre: 'Cumplimiento de SLA',
    descripcion: '¿Qué porcentaje de tickets se resolvieron dentro del tiempo acordado (Service Level Agreement)?',
    formula: '% de tickets cerrados dentro del objetivo de tiempo',
    objetivo: '>= 90%',
    nota: 'Depende de cómo esté configurado el SLA en HubSpot.'
  },
  backlog: {
    sigla: 'Backlog',
    nombre: 'Tickets Abiertos',
    descripcion: 'Cantidad de tickets que aún no se cerraron. Un backlog alto = carga de trabajo pendiente.',
    formula: 'Tickets sin fecha de cierre o en estados abiertos',
    objetivo: 'Tendencia decreciente',
    nota: 'Monitorear la tendencia es más importante que el número absoluto.'
  }
};

// Colores para semáforos y tipos
const COLORS = {
  green: '#22c55e',
  yellow: '#eab308', 
  red: '#ef4444',
  gray: '#9ca3af',
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  // Tipos de agente
  INTERNO: '#3b82f6',    // Azul
  CASA: '#22c55e',       // Verde
  TECNOLOGÍA: '#f59e0b', // Naranja
  OTROS: '#9ca3af',      // Gris
};

const TIPO_LABELS = {
  INTERNO: 'Interno (Capabilia)',
  CASA: 'CASA (Proveedor)',
  TECNOLOGÍA: 'Tecnología (N1)',
  OTROS: 'Otros',
};

// =============================================================================
// Componente Tooltip de KPI
// =============================================================================
const KPITooltip = ({ kpiKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const info = KPI_GLOSSARY[kpiKey];
  
  if (!info) return null;
  
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="text-gray-400 hover:text-blue-500 transition-colors p-0.5"
        aria-label={`Info sobre ${info.sigla}`}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg -left-2 top-6 text-left">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-bold text-blue-600">{info.sigla}</span>
            <span className="text-sm text-gray-700">{info.nombre}</span>
          </div>
          <p className="text-xs text-gray-600 mb-2">{info.descripcion}</p>
          <div className="text-xs space-y-1">
            <div className="flex gap-2">
              <span className="text-gray-400 w-16">Fórmula:</span>
              <span className="text-gray-600">{info.formula}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-gray-400 w-16">Objetivo:</span>
              <span className="text-green-600 font-medium">{info.objetivo}</span>
            </div>
          </div>
          {info.nota && (
            <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
              <strong>Nota:</strong> {info.nota}
            </div>
          )}
          <div className="absolute -top-2 left-3 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Modal de Glosario Completo
// =============================================================================
const GlossaryModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Glosario de KPIs de Atención
          </h2>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-120px)]">
          <div className="mb-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
            <strong>Importante:</strong> Todos los tiempos mostrados son en horas <strong>CALENDARIO</strong> 
            (incluyen fines de semana y feriados), no horas hábiles. Esto se debe a la configuración actual de HubSpot.
          </div>
          
          <div className="space-y-4">
            {Object.entries(KPI_GLOSSARY).map(([key, info]) => (
              <div key={key} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 font-bold rounded text-sm">
                    {info.sigla}
                  </span>
                  <span className="font-medium text-gray-800">{info.nombre}</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{info.descripcion}</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-400 block">Fórmula:</span>
                    <span className="text-gray-700">{info.formula}</span>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <span className="text-gray-400 block">Objetivo:</span>
                    <span className="text-green-700 font-medium">{info.objetivo}</span>
                  </div>
                </div>
                {info.nota && (
                  <div className="mt-2 text-xs text-amber-600 italic">
                    {info.nota}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente de Semáforo
const Semaforo = ({ color, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-6 h-6'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded-full`}
      style={{ backgroundColor: COLORS[color] || COLORS.gray }}
    />
  );
};

// Tarjeta de KPI
const KPICard = ({ title, value, unit, objective, semaforo, gap, note, fillRate, icon: Icon, kpiKey }) => {
  const formatValue = (v, u) => {
    if (v === null || v === undefined) return 'N/A';
    if (u === '%') return `${v}%`;
    if (u === 'horas') return `${v}h`;
    return v;
  };

  const gapText = gap !== null && gap !== undefined 
    ? (gap > 0 ? `+${gap}` : gap)
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <span className="text-sm font-medium text-gray-600">{title}</span>
          {kpiKey && <KPITooltip kpiKey={kpiKey} />}
        </div>
        <Semaforo color={semaforo} />
      </div>
      
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-3xl font-bold text-gray-900">
          {formatValue(value, unit)}
        </span>
        {objective && (
          <span className="text-sm text-gray-500">
            / Obj: {formatValue(objective, unit)}
          </span>
        )}
      </div>
      
      {gapText && (
        <div className={`text-sm font-medium ${gap > 0 ? 'text-red-600' : 'text-green-600'}`}>
          Gap: {gapText}{unit === 'horas' ? 'h' : unit === '%' ? 'pp' : ''}
        </div>
      )}
      
      {fillRate !== undefined && fillRate < 50 && (
        <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Datos parciales ({fillRate}%)
        </div>
      )}
      
      {note && (
        <div className="text-xs text-gray-400 mt-2">{note}</div>
      )}
    </div>
  );
};

// Componente de distribución por Tipo
const TipoDistribution = ({ data }) => {
  if (!data) return null;
  
  const tipos = ['INTERNO', 'CASA', 'TECNOLOGÍA', 'OTROS'];
  
  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4" />
        Quién Atiende (% por Tipo)
      </h3>
      <div className="space-y-2">
        {tipos.map(tipo => {
          const info = data[tipo] || { volumen: 0, pct: 0 };
          return (
            <div key={tipo} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: COLORS[tipo] }}
              />
              <span className="text-xs text-gray-600 w-24">{TIPO_LABELS[tipo]}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${info.pct}%`, 
                    backgroundColor: COLORS[tipo] 
                  }}
                />
              </div>
              <span className="text-xs font-medium w-12 text-right">{info.pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tabla de Agentes
const AgentTable = ({ agents, npsData, sortConfig, onSort }) => {
  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const handleSort = (key) => {
    onSort(key);
  };

  // Merge NPS data
  const agentsWithNPS = agents.map(agent => {
    const nps = npsData?.find(n => n.agente === agent.agente);
    return { ...agent, nps_score: nps?.nps_score, nps_n: nps?.n_responses };
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('agente')}>
              <div className="flex items-center gap-1">Agente {getSortIcon('agente')}</div>
            </th>
            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-100" onClick={() => handleSort('tipo')}>
              <div className="flex items-center gap-1">Tipo {getSortIcon('tipo')}</div>
            </th>
            <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('volumen')}>
              <div className="flex items-center justify-end gap-1">Vol {getSortIcon('volumen')}</div>
            </th>
            <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('frt_median')}>
              <div className="flex items-center justify-end gap-1">FRT {getSortIcon('frt_median')}</div>
            </th>
            <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('ttr_median')}>
              <div className="flex items-center justify-end gap-1">TTR {getSortIcon('ttr_median')}</div>
            </th>
            <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('fcr_pct')}>
              <div className="flex items-center justify-end gap-1">FCR {getSortIcon('fcr_pct')}</div>
            </th>
            <th className="px-3 py-2 text-right cursor-pointer hover:bg-gray-100" onClick={() => handleSort('reopen_pct')}>
              <div className="flex items-center justify-end gap-1">Reopen {getSortIcon('reopen_pct')}</div>
            </th>
            <th className="px-3 py-2 text-right">
              <div className="flex items-center justify-end gap-1">
                NPS
                <span className="text-gray-400 cursor-help" title="Señal indirecta: NPS de alumnos atendidos, no del agente">ⓘ</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {agentsWithNPS.map((agent, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              <td className="px-3 py-2 font-medium">{agent.agente}</td>
              <td className="px-3 py-2">
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: COLORS[agent.tipo] || COLORS.OTROS }}
                >
                  {agent.tipo || 'OTROS'}
                </span>
              </td>
              <td className="px-3 py-2 text-right">{agent.volumen.toLocaleString()}</td>
              <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Semaforo color={agent.frt_semaforo} size="sm" />
                  {agent.frt_median !== null ? `${agent.frt_median}h` : 'N/A'}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Semaforo color={agent.ttr_semaforo} size="sm" />
                  {agent.ttr_median !== null ? `${agent.ttr_median}h` : 'N/A'}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Semaforo color={agent.fcr_semaforo} size="sm" />
                  {agent.fcr_pct !== null ? `${agent.fcr_pct}%` : 'N/A'}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Semaforo color={agent.reopen_semaforo} size="sm" />
                  {agent.reopen_pct !== null ? `${agent.reopen_pct}%` : 'N/A'}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                {agent.nps_score !== null && agent.nps_score !== undefined ? (
                  <span className={agent.nps_score >= 50 ? 'text-green-600' : agent.nps_score >= 0 ? 'text-yellow-600' : 'text-red-600'}>
                    {agent.nps_score > 0 ? '+' : ''}{agent.nps_score}
                    <span className="text-gray-400 text-xs ml-1">(n={agent.nps_n})</span>
                  </span>
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Lista de Intervención
const InterventionList = ({ alumnos }) => {
  if (!alumnos || alumnos.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay alumnos que requieran intervención con los filtros actuales
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-red-50">
          <tr>
            <th className="px-3 py-2 text-left">Alumno</th>
            <th className="px-3 py-2 text-right">Tickets</th>
            <th className="px-3 py-2 text-right">Abiertos</th>
            <th className="px-3 py-2 text-right">TTR Prom</th>
            <th className="px-3 py-2 text-left">Último Ticket</th>
            <th className="px-3 py-2 text-center">Riesgo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {alumnos.map((alumno, idx) => (
            <tr key={idx} className="hover:bg-red-50/50">
              <td className="px-3 py-2">
                <div className="font-medium">{alumno.student_name || alumno.email}</div>
                {alumno.student_name && <div className="text-xs text-gray-500">{alumno.email}</div>}
              </td>
              <td className="px-3 py-2 text-right font-bold text-red-600">{alumno.ticket_count}</td>
              <td className="px-3 py-2 text-right">{alumno.tickets_abiertos}</td>
              <td className="px-3 py-2 text-right">
                {alumno.avg_ttr_hours ? `${alumno.avg_ttr_hours}h` : 'N/A'}
              </td>
              <td className="px-3 py-2 text-xs">{alumno.last_ticket_date?.split(' ')[0]}</td>
              <td className="px-3 py-2 text-center">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  alumno.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                  alumno.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {alumno.risk_level === 'high' ? 'ALTO' : alumno.risk_level === 'medium' ? 'MEDIO' : 'BAJO'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Editor de Objetivos
const ObjectivesEditor = ({ objectives, onSave, loading }) => {
  const values = objectives || {};

  const labels = {
    frt_hours: { label: 'FRT (horas)', desc: 'Tiempo 1ª respuesta' },
    ttr_hours: { label: 'TTR (horas)', desc: 'Tiempo resolución' },
    fcr_percent: { label: 'FCR (%)', desc: 'Resolución 1er contacto' },
    reopen_percent: { label: 'Reapertura (%)', desc: 'Tasa de reapertura' },
    csat_percent: { label: 'CSAT (%)', desc: 'Satisfacción' },
    sla_percent: { label: 'SLA (%)', desc: 'Cumplimiento SLA' },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium flex items-center gap-2">
          <Target className="w-4 h-4" />
          Objetivos de KPIs
        </h3>
        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
          Editar en Config
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Object.entries(labels).map(([key, { label, desc }]) => (
          <div key={key} className="space-y-1">
            <label className="text-xs text-gray-500">{label}</label>
            <div className="text-lg font-bold">
              {values[key] || 'N/A'}
              {key.includes('hours') ? 'h' : key.includes('percent') ? '%' : ''}
            </div>
            <div className="text-xs text-gray-400">{desc}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
        <strong>Nota:</strong> Los tiempos son en horas CALENDARIO, no horas hábiles. Para editar estos valores, usá la pestaña <strong>Configuraciones</strong>.
      </div>
    </div>
  );
};

// Componente Principal
const Atencion360Dashboard = ({ selectedPartnerSlug, selectedPartnerName, getAuthHeaders }) => {
  const [activeTab, setActiveTab] = useState('resumen');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGlossary, setShowGlossary] = useState(false);
  
  // Estado para alternar entre Global e Instancia
  // Si viene sin partner (ya global desde fuera), forzar global
  // Si viene con partner, permitir alternar
  const [viewMode, setViewMode] = useState(selectedPartnerSlug ? 'instance' : 'global');
  
  // Modo global efectivo: o el estado local es 'global', o no hay partner seleccionado
  const isGlobalMode = viewMode === 'global' || !selectedPartnerSlug;
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [agents, setAgents] = useState(null);
  const [npsAgents, setNpsAgents] = useState(null);
  const [tipoEvolution, setTipoEvolution] = useState(null);
  const [volume, setVolume] = useState(null);
  const [intervention, setIntervention] = useState(null);
  const [timeInStages, setTimeInStages] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [filters, setFilters] = useState(null);
  const [fcrRealData, setFcrRealData] = useState(null);
  const [fcrPorAgente, setFcrPorAgente] = useState(null);
  const [csatAgents, setCsatAgents] = useState(null);  // CSAT por agente
  
  // Sort config for agent table
  const [sortConfig, setSortConfig] = useState({ key: 'volumen', direction: 'desc' });
  
  // Period filter
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  
  // Toggle para excluir tickets salientes (contactos proactivos)
  const [excluirSalientes, setExcluirSalientes] = useState(true);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    const params = new URLSearchParams();
    // En modo global, pasar 'all' para obtener agregado de todas las instancias
    const effectiveSlug = isGlobalMode ? 'all' : selectedPartnerSlug;
    params.append('partner_slug', effectiveSlug || 'all');
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);
    // Pasar toggle de excluir salientes a todos los endpoints
    params.append('excluir_salientes', excluirSalientes.toString());
    
    try {
      const headers = getAuthHeaders();
      
      // Fetch all endpoints in parallel
      const [
        summaryRes,
        evolutionRes,
        agentsRes,
        npsAgentsRes,
        tipoEvoRes,
        volumeRes,
        interventionRes,
        stagesRes,
        objectivesRes,
        filtersRes,
        fcrRealRes,
        fcrPorAgenteRes,
        csatAgentsRes
      ] = await Promise.all([
        fetch(`${API_URL}/api/atencion360/summary?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/evolution?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/by-agent?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/nps-by-agent?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/by-tipo-evolution?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/volume?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/alumnos-top?${params}&min_tickets=3`, { headers }),
        fetch(`${API_URL}/api/atencion360/time-in-stages?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/objectives?${params}`, { headers }),
        fetch(`${API_URL}/api/atencion360/filters`, { headers }),
        fetch(`${API_URL}/api/master-servicio/fcr-real?period=ytd${isGlobalMode ? '' : `&instancia=${selectedPartnerSlug}`}`, { headers }),
        fetch(`${API_URL}/api/master-servicio/fcr-por-agente?period=ytd${isGlobalMode ? '' : `&instancia=${selectedPartnerSlug}`}`, { headers }),
        fetch(`${API_URL}/api/atencion360/csat-by-agent?${params}`, { headers }),
      ]);
      
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (evolutionRes.ok) setEvolution(await evolutionRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
      if (npsAgentsRes.ok) setNpsAgents(await npsAgentsRes.json());
      if (tipoEvoRes.ok) setTipoEvolution(await tipoEvoRes.json());
      if (volumeRes.ok) setVolume(await volumeRes.json());
      if (interventionRes.ok) setIntervention(await interventionRes.json());
      if (stagesRes.ok) setTimeInStages(await stagesRes.json());
      if (objectivesRes.ok) setObjectives(await objectivesRes.json());
      if (filtersRes.ok) setFilters(await filtersRes.json());
      if (fcrRealRes.ok) setFcrRealData(await fcrRealRes.json());
      if (fcrPorAgenteRes.ok) setFcrPorAgente(await fcrPorAgenteRes.json());
      if (csatAgentsRes.ok) setCsatAgents(await csatAgentsRes.json());
      
    } catch (err) {
      console.error('Error fetching Atencion360 data:', err);
      setError(err.message);
      toast.error('Error cargando datos de Atención 360');
    } finally {
      setLoading(false);
    }
  }, [selectedPartnerSlug, periodStart, periodEnd, excluirSalientes, getAuthHeaders, isGlobalMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save objectives
  const saveObjectives = async (newObjectives) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(
        `${API_URL}/api/atencion360/objectives?partner_slug=${selectedPartnerSlug || 'default'}`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(newObjectives)
        }
      );
      if (res.ok) {
        toast.success('Objetivos guardados');
        fetchData();
      } else {
        toast.error('Error guardando objetivos');
      }
    } catch (err) {
      toast.error('Error guardando objetivos');
    }
  };

  // Sort agents
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedAgents = agents?.agents ? [...agents.agents].sort((a, b) => {
    const aVal = a[sortConfig.key] ?? -Infinity;
    const bVal = b[sortConfig.key] ?? -Infinity;
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  }) : [];

  // Tab content
  const tabs = [
    { id: 'resumen', label: 'Resumen', icon: Target },
    { id: 'evolucion', label: 'Evolución', icon: TrendingUp },
    { id: 'agentes', label: 'Por Agente', icon: Users },
    { id: 'csat-agente', label: 'CSAT Agente', icon: Star },
    { id: 'tipo', label: 'Por Tipo', icon: Building2 },
    { id: 'intervencion', label: 'Intervención', icon: AlertTriangle },
    { id: 'buscar-email', label: 'Buscar por Email', icon: Search },
    { id: 'objetivos', label: 'Objetivos', icon: Settings },
  ];

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Error: {error}
        <button onClick={fetchData} className="ml-4 text-blue-600 underline">Reintentar</button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="atencion360-dashboard">
      {/* Modal de Glosario */}
      <GlossaryModal isOpen={showGlossary} onClose={() => setShowGlossary(false)} />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Headphones className="w-5 h-5" />
          Atención 360
          {isGlobalMode ? (
            <span className="text-sm font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
              Global (Todas las Instancias)
            </span>
          ) : selectedPartnerName && (
            <span className="text-sm font-normal text-gray-500">— {selectedPartnerName}</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGlossary(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200"
            title="¿Qué significa cada KPI?"
          >
            <BookOpen className="w-4 h-4" />
            Glosario
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Period Filter + Global Toggle */}
      <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg flex-wrap">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-gray-500" />
          
          {/* Toggle Global / Instancia */}
          <div className="flex items-center gap-0 border-r pr-4 mr-2">
            <button
              onClick={() => setViewMode('global')}
              className={`px-3 py-1 text-xs font-medium rounded-l-lg border transition-colors ${
                isGlobalMode 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Global
            </button>
            <button
              onClick={() => selectedPartnerSlug && setViewMode('instance')}
              disabled={!selectedPartnerSlug}
              className={`px-3 py-1 text-xs font-medium rounded-r-lg border-y border-r transition-colors ${
                !isGlobalMode 
                  ? 'bg-blue-600 text-white border-blue-600' 
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              } ${!selectedPartnerSlug ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!selectedPartnerSlug ? 'Seleccione un partner primero' : `Ver solo ${selectedPartnerName}`}
            >
              Instancia
            </button>
          </div>
          
          {/* Toggle Excluir Salientes */}
          <div className="flex items-center gap-2 border-r pr-4 mr-2" data-testid="toggle-excluir-salientes">
            <label 
              className="flex items-center gap-2 cursor-pointer select-none"
              title="Excluir tickets de contactos proactivos (campañas) para ver solo el soporte real"
            >
              <input
                type="checkbox"
                checked={excluirSalientes}
                onChange={(e) => setExcluirSalientes(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Solo soporte</span>
              <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Desde:</label>
            <input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              className="px-2 py-1 border rounded text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Hasta:</label>
            <input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              className="px-2 py-1 border rounded text-sm"
            />
          </div>
          {(periodStart || periodEnd) && (
            <button
              onClick={() => { setPeriodStart(''); setPeriodEnd(''); }}
              className="text-xs text-blue-600 hover:underline"
            >
              Limpiar
            </button>
          )}
        </div>
        
        {/* Info de modo */}
        <div className="flex items-center gap-2">
          {isGlobalMode && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              Mostrando datos de todas las instancias
            </span>
          )}
          {excluirSalientes && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1" title="Los contactos proactivos (campañas de cierre, recordatorios) están excluidos de los KPIs">
              <CheckCircle className="w-3 h-3" />
              KPIs de soporte real
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Tab Content */}
      {!loading && (
        <div className="min-h-[400px]">
          {/* RESUMEN */}
          {activeTab === 'resumen' && summary && (
            <div className="space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KPICard
                  title="FRT (1ª Respuesta)"
                  kpiKey="frt"
                  value={summary.kpis?.frt?.value}
                  unit="horas"
                  objective={summary.kpis?.frt?.objective}
                  semaforo={summary.kpis?.frt?.semaforo}
                  gap={summary.kpis?.frt?.gap}
                  fillRate={summary.kpis?.frt?.fill_rate}
                  note="Mediana. Tiempo calendario."
                  icon={Clock}
                />
                <KPICard
                  title="TTR (Resolución)"
                  kpiKey="ttr"
                  value={summary.kpis?.ttr?.value}
                  unit="horas"
                  objective={summary.kpis?.ttr?.objective}
                  semaforo={summary.kpis?.ttr?.semaforo}
                  gap={summary.kpis?.ttr?.gap}
                  fillRate={summary.kpis?.ttr?.fill_rate}
                  note={`P90: ${summary.kpis?.ttr?.p90 || 'N/A'}h`}
                  icon={CheckCircle}
                />
                <KPICard
                  title="FCR (1er Contacto)"
                  kpiKey="fcr"
                  value={summary.kpis?.fcr?.value}
                  unit="%"
                  objective={summary.kpis?.fcr?.objective}
                  semaforo={summary.kpis?.fcr?.semaforo}
                  gap={summary.kpis?.fcr?.gap}
                  fillRate={summary.kpis?.fcr?.fill_rate}
                  icon={UserCheck}
                />
                <KPICard
                  title="Reapertura"
                  kpiKey="reopen"
                  value={summary.kpis?.reopen?.value}
                  unit="%"
                  objective={summary.kpis?.reopen?.objective}
                  semaforo={summary.kpis?.reopen?.semaforo}
                  gap={summary.kpis?.reopen?.gap}
                  icon={RefreshCw}
                />
                <KPICard
                  title="Escalamiento (N1)"
                  kpiKey="escalamiento"
                  value={summary.kpis?.escalamiento?.value}
                  unit="%"
                  note={`N0: ${summary.kpis?.escalamiento?.n0_count || 0}, N1: ${summary.kpis?.escalamiento?.n1_count || 0}`}
                  icon={TrendingUp}
                />
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{summary.total_tickets?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">
                    {excluirSalientes ? 'Tickets de Soporte' : 'Total Tickets'}
                  </div>
                  {/* Mostrar breakdown cuando se excluyen salientes */}
                  {excluirSalientes && summary.total_salientes > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      ({summary.total_salientes} proactivos excluidos)
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className="text-3xl font-bold text-amber-600">{summary.kpis?.backlog?.value || 0}</div>
                    <KPITooltip kpiKey="backlog" />
                  </div>
                  <div className="text-sm text-gray-500">Backlog (Abiertos)</div>
                  {/* Mostrar breakdown del backlog real */}
                  {excluirSalientes && summary.total_salientes > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      Soporte real
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <div className="text-3xl font-bold text-gray-600">
                      {summary.kpis?.csat?.value != null ? `${summary.kpis.csat.value}%` : 'N/A'}
                    </div>
                    <KPITooltip kpiKey="csat" />
                  </div>
                  <div className="text-sm text-gray-500">
                    CSAT {summary.kpis?.csat?.fill_rate < 5 && '(datos insuf.)'}
                  </div>
                  {/* Detalle: tasa de respuesta */}
                  {summary.kpis?.csat && (
                    <div className="text-xs text-gray-400 mt-1">
                      {summary.kpis.csat.count} resp. ({summary.kpis.csat.fill_rate}% tasa)
                      {summary.kpis.csat.avg_rating && ` · ${summary.kpis.csat.avg_rating}/5`}
                    </div>
                  )}
                </div>
                <TipoDistribution data={agents?.by_tipo} />
              </div>
              
              {/* FCR Real — Objetivo 100% */}
              {fcrRealData && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-200 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-indigo-100 rounded">
                      <Target className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h3 className="font-medium text-indigo-900">FCR Real — Objetivo 100%</h3>
                    <span className="text-xs text-indigo-500 bg-indigo-100 px-2 py-0.5 rounded">
                      Incidencias que DEBEN resolverse en 1er contacto
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-8">
                    {/* FCR Real */}
                    <div className="text-center">
                      <div className="text-xs text-indigo-600 uppercase tracking-wider font-medium mb-1">FCR Real</div>
                      <div className="text-4xl font-bold text-indigo-600">{fcrRealData.fcr_real?.valor || 0}%</div>
                      <div className="text-xs text-indigo-400 mt-1">
                        {fcrRealData.fcr_real?.resueltos_1_contacto?.toLocaleString() || 0} de {fcrRealData.fcr_real?.total?.toLocaleString() || 0} tickets
                      </div>
                      <div className="text-xs text-slate-400 mt-1">Objetivo: 100%</div>
                    </div>
                    
                    {/* BRECHA = 100 - FCR Real */}
                    <div className="text-center border-l border-indigo-200 pl-8">
                      <div className="text-xs text-red-600 uppercase tracking-wider font-medium mb-1">BRECHA</div>
                      <div className={`text-4xl font-bold ${
                        (100 - (fcrRealData.fcr_real?.valor || 0)) > 20 ? 'text-red-600' : 
                        (100 - (fcrRealData.fcr_real?.valor || 0)) > 10 ? 'text-amber-600' : 'text-green-600'
                      }`}>
                        {(100 - (fcrRealData.fcr_real?.valor || 0)).toFixed(1)}%
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        = {((fcrRealData.fcr_real?.total || 0) - (fcrRealData.fcr_real?.resueltos_1_contacto || 0)).toLocaleString()} casos
                      </div>
                      <div className="text-xs text-red-500 mt-1">Resolubles no resueltos en 1er contacto</div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-4 text-center border-t border-indigo-100 pt-3">
                    La <strong>brecha</strong> = 100% − FCR Real. Son {((fcrRealData.fcr_real?.total || 0) - (fcrRealData.fcr_real?.resueltos_1_contacto || 0)).toLocaleString()} tickets 
                    que debían resolverse en primer contacto pero no lo hicieron — foco de capacitación.
                  </p>
                </div>
              )}

              {/* SLA por Tipología — Card nueva */}
              {summary?.kpis?.sla_tipologia?.por_plazo?.length > 0 && (
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg shadow-sm border border-teal-200 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-teal-100 rounded">
                      <Clock className="w-4 h-4 text-teal-600" />
                    </div>
                    <h3 className="font-medium text-teal-900">SLA por Tipología</h3>
                    <span className="text-xs text-teal-500 bg-teal-100 px-2 py-0.5 rounded">
                      Cumplimiento según plazo del Máster de Servicio
                    </span>
                  </div>
                  
                  {/* Global Ponderado */}
                  {(() => {
                    const plazos = summary.kpis.sla_tipologia.por_plazo;
                    const totalEnTiempo = plazos.reduce((sum, p) => sum + (p.en_tiempo || 0), 0);
                    const totalEvaluable = plazos.reduce((sum, p) => sum + (p.total_evaluable || 0), 0);
                    const globalPct = totalEvaluable > 0 ? Math.round(totalEnTiempo / totalEvaluable * 1000) / 10 : null;
                    const globalSemaforo = globalPct >= 90 ? 'green' : globalPct >= 70 ? 'yellow' : 'red';
                    
                    return (
                      <div className="mb-4 p-3 bg-white/60 rounded-lg border border-teal-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-teal-600 uppercase tracking-wider font-medium">Global Ponderado</div>
                            <div className="text-xs text-slate-500">Σ en tiempo / Σ evaluables de todos los plazos</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`text-3xl font-bold ${
                              globalSemaforo === 'green' ? 'text-green-600' : 
                              globalSemaforo === 'yellow' ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {globalPct != null ? `${globalPct}%` : 'N/A'}
                            </div>
                            <div className={`w-3 h-3 rounded-full ${
                              globalSemaforo === 'green' ? 'bg-green-500' : 
                              globalSemaforo === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                            }`} />
                          </div>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {totalEnTiempo.toLocaleString()} de {totalEvaluable.toLocaleString()} tickets en tiempo
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Tabla de plazos */}
                  <div className="overflow-hidden rounded-lg border border-teal-100">
                    <table className="w-full text-sm">
                      <thead className="bg-teal-100/50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium text-teal-800">Plazo</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">Cumplimiento</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">Objetivo</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">En Tiempo</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">Vencidos</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">Sin Dato</th>
                          <th className="text-center py-2 px-3 font-medium text-teal-800">Semáforo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-teal-100">
                        {summary.kpis.sla_tipologia.por_plazo.map((plazo, idx) => (
                          <tr key={plazo.plazo} className={idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}>
                            <td className="py-2 px-3 font-medium text-slate-700">
                              {plazo.plazo === 'FCR' ? 'FCR (1er contacto)' : plazo.plazo}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span className={`font-bold ${
                                plazo.pct_cumplimiento >= plazo.objetivo ? 'text-green-600' :
                                plazo.pct_cumplimiento >= 70 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {plazo.pct_cumplimiento != null ? `${plazo.pct_cumplimiento}%` : 'N/A'}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-center text-slate-500">
                              {plazo.objetivo}%
                            </td>
                            <td className="py-2 px-3 text-center text-green-600">
                              {plazo.en_tiempo?.toLocaleString() || 0}
                            </td>
                            <td className="py-2 px-3 text-center text-red-600">
                              {plazo.vencidos?.toLocaleString() || 0}
                            </td>
                            <td className="py-2 px-3 text-center text-slate-400">
                              {plazo.sin_dato?.toLocaleString() || 0}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <div className={`w-4 h-4 rounded-full mx-auto ${
                                plazo.semaforo === 'green' ? 'bg-green-500' :
                                plazo.semaforo === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                              }`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    💡 <strong>FCR</strong> usa campo is_one_touch • Otros plazos usan time_to_close vs horas del Máster de Servicio
                  </p>
                </div>
              )}
            </div>
          )}

          {/* EVOLUCIÓN */}
          {activeTab === 'evolucion' && evolution && (
            <div className="space-y-6">
              {/* FRT Evolution */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4">Evolución FRT (Tiempo 1ª Respuesta)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={evolution.evolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="frt_median" name="FRT (h)" stroke={COLORS.primary} strokeWidth={2} dot />
                    <ReferenceLine y={objectives?.objectives?.frt_hours || 8} stroke={COLORS.green} strokeDasharray="5 5" label="Objetivo" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* TTR Evolution */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4">Evolución TTR (Tiempo Resolución)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={evolution.evolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="ttr_median" name="TTR (h)" stroke={COLORS.secondary} strokeWidth={2} dot />
                    <ReferenceLine y={objectives?.objectives?.ttr_hours || 48} stroke={COLORS.green} strokeDasharray="5 5" label="Objetivo" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* FCR Evolution */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4">Evolución FCR y Reapertura</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={evolution.evolution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="fcr_pct" name="FCR (%)" stroke={COLORS.green} strokeWidth={2} dot />
                    <Line type="monotone" dataKey="reopen_pct" name="Reapertura (%)" stroke={COLORS.red} strokeWidth={2} dot />
                    <ReferenceLine y={objectives?.objectives?.fcr_percent || 70} stroke={COLORS.green} strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Tipo Evolution */}
              {tipoEvolution && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <h3 className="font-medium mb-4">Evolución % por Tipo de Agente</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={tipoEvolution.evolution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="CASA_pct" name="CASA %" stackId="1" stroke={COLORS.CASA} fill={COLORS.CASA} fillOpacity={0.6} />
                      <Area type="monotone" dataKey="INTERNO_pct" name="Interno %" stackId="1" stroke={COLORS.INTERNO} fill={COLORS.INTERNO} fillOpacity={0.6} />
                      <Area type="monotone" dataKey="TECNOLOGÍA_pct" name="Tecnología %" stackId="1" stroke={COLORS.TECNOLOGÍA} fill={COLORS.TECNOLOGÍA} fillOpacity={0.6} />
                      <Area type="monotone" dataKey="OTROS_pct" name="Otros %" stackId="1" stroke={COLORS.OTROS} fill={COLORS.OTROS} fillOpacity={0.6} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* POR AGENTE */}
          {activeTab === 'agentes' && agents && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">KPIs por Agente ({agents.total_agents})</h3>
                  <div className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    NPS es señal indirecta (experiencia global del alumno)
                  </div>
                </div>
                <AgentTable 
                  agents={sortedAgents} 
                  npsData={npsAgents?.agents}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
              
              {/* FCR por Agente - Capacitación */}
              {fcrPorAgente?.agentes?.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium flex items-center gap-2">
                        <Target className="w-5 h-5 text-indigo-600" />
                        FCR por Agente — Necesidad de Capacitación
                      </h3>
                      <p className="text-sm text-gray-500">
                        Objetivo: FCR Real = 100% en incidencias que deben resolverse en primer contacto
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">FCR Real Global</div>
                      <div className="text-2xl font-bold text-indigo-600">
                        {fcrPorAgente.resumen_global?.fcr_real_global || 0}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2 font-medium">Agente</th>
                          <th className="text-right p-2 font-medium">Tickets</th>
                          <th className="text-right p-2 font-medium">FCR Bruto</th>
                          <th className="text-right p-2 font-medium">
                            <span className="text-indigo-600">FCR Real</span>
                            <span className="text-xs text-gray-400 ml-1">(obj 100%)</span>
                          </th>
                          <th className="text-right p-2 font-medium">
                            Brecha
                            <span className="text-xs text-gray-400 ml-1">(100% - Real)</span>
                          </th>
                          <th className="text-center p-2 font-medium">Prioridad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fcrPorAgente.agentes.slice(0, 15).map((agente, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-2 font-medium">{agente.agente}</td>
                            <td className="p-2 text-right text-gray-600">
                              {agente.total_tickets}
                              <span className="text-xs text-gray-400 ml-1">
                                ({agente.tickets_fcr_aplica} FCR)
                              </span>
                            </td>
                            <td className="p-2 text-right text-gray-600">{agente.fcr_bruto}%</td>
                            <td className="p-2 text-right">
                              <span className={`font-medium ${agente.fcr_real >= 80 ? 'text-green-600' : agente.fcr_real >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                                {agente.fcr_real !== null ? `${agente.fcr_real}%` : '—'}
                              </span>
                            </td>
                            <td className="p-2 text-right">
                              <span className={`font-bold ${agente.brecha > 30 ? 'text-red-600' : agente.brecha > 15 ? 'text-amber-600' : 'text-green-600'}`}>
                                {agente.brecha !== null ? `${agente.brecha}pp` : '—'}
                              </span>
                            </td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                agente.prioridad_capacitacion === 'alta' ? 'bg-red-100 text-red-700' :
                                agente.prioridad_capacitacion === 'media' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {agente.prioridad_capacitacion}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    <strong>FCR Real</strong> = % de primer contacto solo sobre incidencias que DEBEN resolverse en 1er contacto (según máster). 
                    <strong> Brecha</strong> = distancia al 100%. Mayor brecha = mayor necesidad de capacitación.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* CSAT POR AGENTE */}
          {activeTab === 'csat-agente' && (
            <div className="space-y-4">
              {/* Summary Card */}
              {csatAgents?.summary && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow-sm border border-amber-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-600" />
                      <h3 className="font-medium text-amber-900">CSAT por Agente — Satisfacción del Alumno</h3>
                    </div>
                    {/* Botón de descarga */}
                    <button
                      onClick={async () => {
                        try {
                          const params = new URLSearchParams();
                          const effectiveSlug = isGlobalMode ? 'all' : selectedPartnerSlug;
                          params.append('partner_slug', effectiveSlug || 'all');
                          if (periodStart) params.append('period_start', periodStart);
                          if (periodEnd) params.append('period_end', periodEnd);
                          params.append('excluir_salientes', excluirSalientes.toString());
                          params.append('format', 'xlsx');
                          
                          const url = `${API_URL}/api/atencion360/csat-by-agent/export?${params}`;
                          await secureExportDownload({ url, filename: 'csat_por_agente.xlsx' });
                          toast.success('CSAT por Agente descargado');
                        } catch (err) {
                          if (err.message !== 'cancelado') {
                            toast.error('Error al descargar');
                          }
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors"
                      data-testid="export-csat-agente"
                    >
                      <Download className="w-4 h-4" />
                      Descargar Excel
                    </button>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white/70 rounded-lg p-3">
                      <div className="text-2xl font-bold text-amber-700">
                        {csatAgents.summary.global_csat_percent != null 
                          ? `${csatAgents.summary.global_csat_percent}%` 
                          : 'N/A'}
                      </div>
                      <div className="text-sm text-amber-600">CSAT Global</div>
                      <div className="text-xs text-gray-500">% satisfechos (≥4/5)</div>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <div className="text-2xl font-bold text-amber-700">
                        {csatAgents.summary.global_response_rate}%
                      </div>
                      <div className="text-sm text-amber-600">Tasa Respuesta</div>
                      <div className="text-xs text-gray-500">encuestas / tickets</div>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <div className="text-2xl font-bold text-gray-700">
                        {csatAgents.summary.total_csat_responses.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Respuestas CSAT</div>
                      <div className="text-xs text-gray-500">encuestas completadas</div>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3">
                      <div className="text-2xl font-bold text-gray-700">
                        {csatAgents.summary.total_tickets.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Tickets Totales</div>
                      <div className="text-xs text-gray-500">período seleccionado</div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-amber-700">
                    <strong>CSAT</strong> = % de respuestas con rating ≥4 (Top-box, escala 1-5). 
                    <strong> Tasa respuesta</strong> = encuestas completadas / tickets. 
                    Tasa baja = feedback insuficiente para evaluar calidad.
                  </p>
                </div>
              )}
              
              {/* Agent Table */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  Detalle por Agente ({csatAgents?.total_agents || 0})
                </h3>
                
                {csatAgents?.agents?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Agente</th>
                          <th className="px-3 py-2 text-left font-medium">Tipo</th>
                          <th className="px-3 py-2 text-right font-medium">Tickets</th>
                          <th className="px-3 py-2 text-right font-medium">
                            <span className="flex items-center justify-end gap-1">
                              Tasa Resp.
                              <span className="text-gray-400 cursor-help" title="% de tickets con encuesta CSAT respondida">ⓘ</span>
                            </span>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">Respuestas</th>
                          <th className="px-3 py-2 text-right font-medium">
                            <span className="flex items-center justify-end gap-1">
                              CSAT %
                              <span className="text-gray-400 cursor-help" title="% satisfechos (rating ≥4 de 5). Obj: ≥85%">ⓘ</span>
                            </span>
                          </th>
                          <th className="px-3 py-2 text-right font-medium">Prom /5</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {csatAgents.agents.map((agent, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium">{agent.agente}</td>
                            <td className="px-3 py-2">
                              <span 
                                className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: COLORS[agent.tipo] || COLORS.OTROS }}
                              >
                                {agent.tipo || 'OTROS'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">{agent.total_tickets.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <span className={`w-2 h-2 rounded-full ${
                                  agent.csat_response_rate >= 10 ? 'bg-green-500' :
                                  agent.csat_response_rate >= 5 ? 'bg-yellow-500' :
                                  agent.total_tickets >= 10 ? 'bg-red-500' : 'bg-gray-300'
                                }`} />
                                {agent.csat_response_rate}%
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-gray-600">{agent.csat_responses}</td>
                            <td className="px-3 py-2 text-right">
                              {agent.csat_percent != null ? (
                                <div className="flex items-center justify-end gap-1">
                                  <span className={`w-2 h-2 rounded-full ${
                                    agent.csat_percent >= 85 ? 'bg-green-500' :
                                    agent.csat_percent >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`} />
                                  <span className={`font-medium ${
                                    agent.csat_percent >= 85 ? 'text-green-600' :
                                    agent.csat_percent >= 70 ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    {agent.csat_percent}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-500">
                              {agent.csat_avg != null ? agent.csat_avg : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Star className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No hay datos de CSAT para este período</p>
                    <p className="text-sm text-gray-400">Las encuestas CSAT se envían después de cerrar cada ticket</p>
                  </div>
                )}
              </div>
              
              {/* Note */}
              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <strong>Interpretación:</strong>
                <ul className="list-disc ml-4 mt-1 space-y-1">
                  <li><strong>Tasa de respuesta alta + CSAT alto</strong> = Agente genera feedback positivo (ideal)</li>
                  <li><strong>Tasa de respuesta alta + CSAT bajo</strong> = Hay insatisfacción real, necesita atención</li>
                  <li><strong>Tasa de respuesta baja</strong> = Pocos datos, difícil evaluar calidad (priorizar aumentar feedback)</li>
                </ul>
              </div>
            </div>
          )}

          {/* POR TIPO */}
          {activeTab === 'tipo' && (
            <div className="space-y-4">
              {/* Distribution Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['INTERNO', 'CASA', 'TECNOLOGÍA', 'OTROS'].map(tipo => {
                  const data = agents?.by_tipo?.[tipo] || { volumen: 0, pct: 0 };
                  return (
                    <div key={tipo} className="bg-white rounded-lg shadow-sm border p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[tipo] }} />
                        <span className="font-medium">{TIPO_LABELS[tipo]}</span>
                      </div>
                      <div className="text-3xl font-bold" style={{ color: COLORS[tipo] }}>
                        {data.pct}%
                      </div>
                      <div className="text-sm text-gray-500">
                        {data.volumen.toLocaleString()} tickets
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tipo Evolution Chart */}
              {tipoEvolution && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <h3 className="font-medium mb-4">Tendencia: Quién Atiende</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Objetivo: CASA (proveedor) debería cubrir la mayoría. Interno (Capabilia) solo casos específicos.
                  </p>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={tipoEvolution.evolution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="CASA_pct" name="CASA (Proveedor)" stackId="1" stroke={COLORS.CASA} fill={COLORS.CASA} fillOpacity={0.7} />
                      <Area type="monotone" dataKey="INTERNO_pct" name="Interno (Capabilia)" stackId="1" stroke={COLORS.INTERNO} fill={COLORS.INTERNO} fillOpacity={0.7} />
                      <Area type="monotone" dataKey="TECNOLOGÍA_pct" name="Tecnología (N1)" stackId="1" stroke={COLORS.TECNOLOGÍA} fill={COLORS.TECNOLOGÍA} fillOpacity={0.7} />
                      <Area type="monotone" dataKey="OTROS_pct" name="Otros" stackId="1" stroke={COLORS.OTROS} fill={COLORS.OTROS} fillOpacity={0.7} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Monthly Table */}
              {tipoEvolution?.evolution && (
                <div className="bg-white rounded-lg shadow-sm border p-4 overflow-x-auto">
                  <h3 className="font-medium mb-4">Detalle Mensual</h3>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Mes</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">CASA</th>
                        <th className="px-3 py-2 text-right">Interno</th>
                        <th className="px-3 py-2 text-right">Tecnología</th>
                        <th className="px-3 py-2 text-right">Otros</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {tipoEvolution.evolution.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{row.month}</td>
                          <td className="px-3 py-2 text-right">{row.total}</td>
                          <td className="px-3 py-2 text-right">
                            <span style={{ color: COLORS.CASA }}>{row.CASA_pct}%</span>
                            <span className="text-gray-400 text-xs ml-1">({row.CASA})</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span style={{ color: COLORS.INTERNO }}>{row.INTERNO_pct}%</span>
                            <span className="text-gray-400 text-xs ml-1">({row.INTERNO})</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span style={{ color: COLORS.TECNOLOGÍA }}>{row.TECNOLOGÍA_pct}%</span>
                            <span className="text-gray-400 text-xs ml-1">({row.TECNOLOGÍA})</span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span style={{ color: COLORS.OTROS }}>{row.OTROS_pct}%</span>
                            <span className="text-gray-400 text-xs ml-1">({row.OTROS})</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* INTERVENCIÓN */}
          {activeTab === 'intervencion' && (
            <div className="space-y-4">
              {/* Lista de Intervención */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Alumnos que Requieren Intervención
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Alumnos con 3+ tickets. Priorizar contacto proactivo.
                </p>
                <InterventionList alumnos={intervention?.alumnos} />
              </div>

              {/* Volume by Canal */}
              {volume && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg shadow-sm border p-4">
                    <h3 className="font-medium mb-4">Volumen por Canal</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={volume.by_canal} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="canal" type="category" tick={{ fontSize: 12 }} width={80} />
                        <Tooltip />
                        <Bar dataKey="count" fill={COLORS.primary} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border p-4">
                    <h3 className="font-medium mb-4">Volumen por Motivo (Clase)</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={volume.by_clase?.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="clase" type="category" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip />
                        <Bar dataKey="count" fill={COLORS.secondary} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Time in Stages */}
              {timeInStages?.stages?.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <h3 className="font-medium mb-4">Tiempo por Etapa del Pipeline</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Dónde se traban los tickets (promedio en horas)
                  </p>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={timeInStages.stages}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="avg_hours" name="Horas promedio" fill={COLORS.primary} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* BUSCAR POR EMAIL */}
          {activeTab === 'buscar-email' && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="font-medium mb-4 flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-500" />
                  Buscar Tickets por Email del Alumno
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Busca los tickets de HubSpot asociados a un email (match exacto o parcial, sin distinguir mayúsculas).
                </p>
                <TicketSearchByEmail selectedPartnerSlug={selectedPartnerSlug} getAuthHeaders={getAuthHeaders} />
              </div>
            </div>
          )}

          {/* OBJETIVOS */}
          {activeTab === 'objetivos' && (
            <div className="space-y-4">
              <ObjectivesEditor 
                objectives={objectives?.objectives} 
                onSave={saveObjectives}
                loading={loading}
              />
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                <strong>Estándares de industria (referencia):</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li>FRT: &lt; 8 horas (calendario)</li>
                  <li>TTR: &lt; 48 horas (calendario)</li>
                  <li>FCR: &gt;= 70%</li>
                  <li>Reapertura: &lt; 5%</li>
                  <li>CSAT: &gt;= 85%</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Atencion360Dashboard;
